import { Box, Chip, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { IncidentDetail } from "../../types/incidentDetail";
import SourceCountBlock, { Mono } from "../CategoryDetail/SourceCountBlock";
import { codeWords } from "../Detail/format";

export const UNNAMED_INCIDENT = "Unnamed incident";

const chipSx = { height: 24, fontSize: "0.75rem" } as const;

/**
 * The head of the incident view: the name, the chips for its types,
 * severity and countries, and how many sources there are, with their
 * timeline and when the incident was first reported.
 */
const IncidentHead: React.FC<{ incident: IncidentDetail }> = ({ incident }) => {
  const chips = [
    ...incident.types.map(codeWords),
    ...(incident.severity
      ? [`severity: ${codeWords(incident.severity)}`]
      : []),
    ...incident.countries,
  ];
  const sourceDates = useMemo(
    () => incident.sources.items.map((source) => source.date),
    [incident.sources.items],
  );

  return (
    <Box
      component="header"
      sx={{ px: 3, pt: 3, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{
          overflowWrap: "anywhere",
          color: incident.name ? "text.primary" : "text.secondary",
          fontStyle: incident.name ? "normal" : "italic",
        }}
      >
        {incident.name ?? UNNAMED_INCIDENT}
      </Typography>

      {chips.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {chips.map((chip) => (
            <Chip key={chip} label={chip} variant="outlined" sx={chipSx} />
          ))}
        </Box>
      )}

      <SourceCountBlock
        count={incident.sources.total}
        dates={sourceDates}
        footnote={
          incident.published && (
            <>
              first reported <Mono>{incident.published}</Mono>
            </>
          )
        }
      />
    </Box>
  );
};

export default IncidentHead;
